import React from 'react';
import { UnscheduledEvent } from '../types';
import {
  ERTS_REMOVED_CONFIDENCE,
  EVENT_STATUS,
  DAMAGE_IROP_FLAG,
  ONGOING_DELAY_LABEL,
} from '../constants';
import MovedFromDot from './MovedFromDot';
import { getIropDisplayEntries } from '../utils/iropUtils';
import { useDamageLocationStatus } from '../hooks/useDamageLocationStatus';
import { getTimeFieldAlertState } from '../utils/eventAlerts';
import { isNextScheduledFlightEarly } from './eventModal/nextScheduledFlight';
import { formatAtaCode } from '../services/ataCodeService';
import { useColumnSort, SortAccessor } from '../utils/useColumnSort';
import { usePagination } from '../utils/usePagination';
import { isLockedByOther } from '../services/lockService';
import TablePager from './TablePager';

/**
 * Maps a time-field alert state to its highlight CSS class.
 */
function alertClass(state: 'overdue' | 'warning' | null): string {
  if (state === 'overdue') return 'time-alert-red';
  if (state === 'warning') return 'time-alert-yellow';
  return '';
}

/**
 * Props for the EventsTable component.
 */
interface EventsTableProps {
  events: UnscheduledEvent[];
  onView: (event: UnscheduledEvent) => void;
  onEdit: (event: UnscheduledEvent) => void;
  hideProjectedErts?: boolean;
  showRecordType?: boolean;
  /**
   * When true, the Next Advise and ERTS/RTS cells render without the
   * overdue/warning color highlight. Used by History, where every record is
   * completed and the time-based alert colors are meaningless noise.
   */
  hideTimeAlerts?: boolean;
  /**
   * When set, the table renders at most this many rows per page with pager
   * controls. Used by large views (History) to keep first paint fast. Omit for
   * small active-event views, which render every row.
   */
  pageSize?: number;
  /** Map of eventId → alias currently holding that event's edit lock. */
  lockOwners?: Record<string, string>;
  /** The current user's alias, so their own lock never disables their Edit button. */
  currentUserAlias?: string;
  /**
   * When true, the DAMAGE IROP label is rendered in purple for any damage event
   * whose SkyDent Damage Location is not yet completed (looked up per event).
   * Enabled only for the homepage active view to avoid lookups on large views.
   */
  flagIncompleteDamageLocation?: boolean;
}

/**
 * Returns the latest (chronologically most recent) Projected ERTS across all of
 * a record's defects. Each defect carries its own Projected ERTS in the
 * `projectedErts` map (keyed by defect id); when a record has more than one
 * defect, the most recent projected return time represents the record. Stored
 * values are `YYYY-MM-DD HH:MM` (or date-only), which sort chronologically as a
 * lexical comparison. Returns '' when no projection exists.
 */
function getLatestProjectedErts(event: UnscheduledEvent): string {
  if (!event.projectedErts) return '';
  let latest = '';
  for (const value of Object.values(event.projectedErts)) {
    if (value && value > latest) latest = value;
  }
  return latest;
}

/**
 * Formats a datetime string as date on top, time below.
 */
function formatDateTime(datetime: string): React.ReactNode {
  if (!datetime) return '—';
  const cleaned = datetime.trim();
  const parts = cleaned.split(/[T ]/);
  const date = parts[0] || '';
  const time = parts[1] ? parts[1].substring(0, 5) : '';
  return (
    <div className="datetime-stacked">
      <span className="datetime-date">{date}</span>
      <span className="datetime-time">{time}z</span>
    </div>
  );
}

/**
 * Sort accessors for the events table columns. Date/time fields use their raw
 * Zulu string, which sorts chronologically as a lexical comparison.
 */
const EVENTS_SORT_ACCESSORS: Record<string, SortAccessor<UnscheduledEvent>> = {
  recordType: (e) => e.recordType || 'Unscheduled',
  carrier: (e) => e.carrier || '',
  tailNumber: (e) => e.tailNumber || '',
  station: (e) => e.station || '',
  nextFlight: (e) => e.nextScheduledFlightNumber || '',
  status: (e) => e.status || '',
  nextAdvise: (e) => e.nextAdviseTimeZulu || '',
  ertsRts: (e) => e.returnToServiceTimeZulu || '',
  confidence: (e) =>
    e.returnToServiceConfidence && e.returnToServiceConfidence !== ERTS_REMOVED_CONFIDENCE
      ? e.returnToServiceConfidence
      : '',
  delay: (e) => e.delayTimeMinutes || 0,
  std: (e) => e.nextScheduledFlightTimeZulu || '',
};

/**
 * Main data table displaying active unscheduled events.
 */
const EventsTable: React.FC<EventsTableProps> = ({
  events,
  onView,
  onEdit,
  hideProjectedErts,
  showRecordType,
  hideTimeAlerts,
  pageSize,
  lockOwners,
  currentUserAlias,
  flagIncompleteDamageLocation,
}) => {
  const damageLocationMissingIds = useDamageLocationStatus(
    events,
    Boolean(flagIncompleteDamageLocation),
  );
  const { toggleSort, sortData, arrowFor } = useColumnSort<UnscheduledEvent>(EVENTS_SORT_ACCESSORS);
  // Sort the full set first so paging reflects the active sort order, then
  // slice to the current page. Unpaged views fall back to a single page.
  const sortedRows = sortData(events);
  const effectivePageSize = pageSize ?? Math.max(sortedRows.length, 1);
  const { pageItems, pageIndex, pageCount, rangeStart, rangeEnd, totalItems, prevPage, nextPage } =
    usePagination(sortedRows, effectivePageSize);
  const rows = pageItems;
  return (
    <div className="events-section">
      <div className="events-count">
        {events.length} Active Record{events.length !== 1 ? 's' : ''}
      </div>
      <div className="table-wrapper">
        <table className="events-table">
          <thead>
            <tr>
              {showRecordType && (
                <th className="sortable" onClick={() => toggleSort('recordType')}>
                  Record Type <span className="sort-arrow">{arrowFor('recordType')}</span>
                </th>
              )}
              <th className="sortable" onClick={() => toggleSort('status')}>
                Status <span className="sort-arrow">{arrowFor('status')}</span>
              </th>
              <th className="sortable" onClick={() => toggleSort('carrier')}>
                Carrier <span className="sort-arrow">{arrowFor('carrier')}</span>
              </th>
              <th className="sortable" onClick={() => toggleSort('tailNumber')}>
                Tail Number <span className="sort-arrow">{arrowFor('tailNumber')}</span>
              </th>
              <th className="sortable" onClick={() => toggleSort('station')}>
                Station <span className="sort-arrow">{arrowFor('station')}</span>
              </th>
              <th>Defect</th>
              <th>ATA Code</th>
              <th>IROP</th>
              <th className="sortable" onClick={() => toggleSort('nextAdvise')}>
                Next Advise <span className="sort-arrow">{arrowFor('nextAdvise')}</span>
              </th>
              {!hideProjectedErts && (
                <th>
                  Projected ERTS <span className="status-badge status-pending">Beta</span>
                </th>
              )}
              <th className="sortable" onClick={() => toggleSort('ertsRts')}>
                ERTS/RTS <span className="sort-arrow">{arrowFor('ertsRts')}</span>
              </th>
              <th className="sortable" onClick={() => toggleSort('confidence')}>
                Confidence <span className="sort-arrow">{arrowFor('confidence')}</span>
              </th>
              <th className="sortable" onClick={() => toggleSort('delay')}>
                Delay (min) <span className="sort-arrow">{arrowFor('delay')}</span>
              </th>
              <th>Mx Barrier</th>
              <th className="sortable" onClick={() => toggleSort('nextFlight')}>
                Next Flight <span className="sort-arrow">{arrowFor('nextFlight')}</span>
              </th>
              <th className="sortable" onClick={() => toggleSort('std')}>
                STD <span className="sort-arrow">{arrowFor('std')}</span>
              </th>
              <th>Spare Event</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((event) => (
              <tr key={event.eventId}>
                {showRecordType && <td>{event.recordType || 'Unscheduled'}</td>}
                <td>
                  <span
                    className={`status-badge status-${event.status.toLowerCase().replace('_', '-')}`}
                  >
                    {event.status.replace('_', ' ')}
                  </span>
                  {event.movedFrom && <MovedFromDot fromRecordType={event.movedFrom} />}
                </td>
                <td>{event.carrier}</td>
                <td>
                  <span className="aircraft-link">{event.tailNumber}</span>
                </td>
                <td>{event.station}</td>
                <td className="discrepancy-cell">
                  {event.defects && event.defects.length > 0
                    ? event.defects.map((d) => (
                        <div key={d.id} className="defect-entry">
                          {d.id}) {d.text}
                        </div>
                      ))
                    : '—'}
                </td>
                <td className="ata-code-cell">
                  {event.defects && event.defects.length > 0 && event.selectedChapters
                    ? event.defects.map((d) => {
                        const chapter = event.selectedChapters?.[d.id] || '';
                        const section = event.selectedSections?.[d.id] || '';
                        if (!chapter)
                          return (
                            <div key={d.id} className="ata-entry">
                              —
                            </div>
                          );
                        return (
                          <div key={d.id} className="ata-entry">
                            {formatAtaCode(chapter, section)}
                          </div>
                        );
                      })
                    : '—'}
                </td>
                <td>
                  {(() => {
                    const iropEntries = getIropDisplayEntries(event);
                    if (iropEntries.length === 0) {
                      return <span className="irop-badge irop-no">—</span>;
                    }
                    return (
                      <div className="irop-list">
                        {iropEntries.map((entry) => {
                          const isDamageIncomplete =
                            entry.flag === DAMAGE_IROP_FLAG &&
                            damageLocationMissingIds.has(event.eventId);
                          return (
                            <span
                              key={entry.flag}
                              className={`irop-badge irop-yes irop-line${isDamageIncomplete ? ' irop-damage-incomplete-location' : ''}`}
                            >
                              {entry.label}
                            </span>
                          );
                        })}
                      </div>
                    );
                  })()}
                </td>
                <td
                  className={
                    hideTimeAlerts
                      ? ''
                      : alertClass(getTimeFieldAlertState(event.nextAdviseTimeZulu))
                  }
                >
                  {formatDateTime(event.nextAdviseTimeZulu)}
                </td>
                {!hideProjectedErts && (
                  <td>
                    {getLatestProjectedErts(event)
                      ? formatDateTime(getLatestProjectedErts(event))
                      : '—'}
                  </td>
                )}
                <td
                  className={
                    hideTimeAlerts
                      ? ''
                      : alertClass(getTimeFieldAlertState(event.returnToServiceTimeZulu))
                  }
                >
                  {formatDateTime(event.returnToServiceTimeZulu)}
                </td>
                <td>
                  {event.returnToServiceConfidence &&
                  event.returnToServiceConfidence !== ERTS_REMOVED_CONFIDENCE
                    ? event.returnToServiceConfidence
                    : '—'}
                </td>
                <td>
                  {event.delay && !(Number(event.delayTimeMinutes) > 0)
                    ? ONGOING_DELAY_LABEL
                    : event.delayTimeMinutes}
                </td>
                <td className="mx-barrier-cell">
                  {event.barriers && event.barriers.filter((b) => !b.endTime).length > 0
                    ? event.barriers
                        .filter((b) => !b.endTime)
                        .map((b, i) => {
                          const start = new Date(
                            b.startTime.replace(' ', 'T') + (b.startTime.includes('Z') ? '' : 'Z'),
                          );
                          const diffMs = Date.now() - start.getTime();
                          const hoursNum = diffMs > 0 ? diffMs / (1000 * 60 * 60) : 0;
                          const hours = hoursNum.toFixed(2);
                          // Suppress the >24h overdue flash once the event is
                          // closed (Completed) — the barrier is historical, not
                          // an active alert.
                          const isOverdue =
                            hoursNum >= 24 && event.status !== EVENT_STATUS.COMPLETED;
                          return (
                            <div
                              key={i}
                              className={`mx-barrier-entry ${isOverdue ? 'mx-barrier-flash' : ''}`}
                            >
                              {b.type} ({hours}h)
                            </div>
                          );
                        })
                    : '—'}
                </td>
                <td
                  className={
                    !hideTimeAlerts && isNextScheduledFlightEarly(event) ? 'next-flight-early' : ''
                  }
                >
                  {event.nextScheduledFlightNumber || '—'}
                </td>
                <td
                  className={
                    !hideTimeAlerts && isNextScheduledFlightEarly(event) ? 'next-flight-early' : ''
                  }
                >
                  {formatDateTime(event.nextScheduledFlightTimeZulu)}
                </td>
                <td>—</td>
                <td className="actions-cell">
                  <button
                    className="action-btn view-btn"
                    type="button"
                    aria-label="View event"
                    onClick={() => onView(event)}
                  >
                    &#128065;
                  </button>
                  <button
                    className="action-btn edit-btn"
                    type="button"
                    aria-label="Edit event"
                    disabled={isLockedByOther(lockOwners?.[event.eventId], currentUserAlias || '')}
                    title={
                      isLockedByOther(lockOwners?.[event.eventId], currentUserAlias || '')
                        ? `Being edited by ${lockOwners?.[event.eventId]}`
                        : 'Edit event'
                    }
                    onClick={() => onEdit(event)}
                  >
                    &#9998;
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <TablePager
        pageIndex={pageIndex}
        pageCount={pageCount}
        rangeStart={rangeStart}
        rangeEnd={rangeEnd}
        totalItems={totalItems}
        onPrev={prevPage}
        onNext={nextPage}
      />
    </div>
  );
};

export default EventsTable;
